/**
 * Bold/italic detection from PostScript font names.
 *
 * pdf.js gives us a font reference like "g_d0_f1" via the TextItem fontName,
 * but `page.commonObjs.get(fontName)` resolves it to the real PostScript name
 * (e.g., "Arial-BoldMT", "TimesNewRomanPS-Italic"). The patterns below match
 * the common style markers across font families seen in the wild.
 */
const BOLD_PATTERNS = /(Bold|Black|Heavy|DemiBold|SemiBold|ExtraBold|UltraBold|Demi|Semi(?!light)|Wt0?[789]|Wt1[01])/i;
const ITALIC_PATTERNS = /(Italic|Oblique|Slant|It$|It[-_])/i;
/** Inspect a resolved font name and report style flags. */
export function classifyFont(fontName) {
    if (!fontName)
        return { bold: false, italic: false };
    return {
        bold: BOLD_PATTERNS.test(fontName),
        italic: ITALIC_PATTERNS.test(fontName),
    };
}
/**
 * Per-page font statistics. We use median (not mean) because PDFs often have
 * a long tail of larger items (titles, callouts, page numbers) that skew mean.
 */
export function computePageFontStats(items) {
    const byPage = new Map();
    for (const item of items) {
        const arr = byPage.get(item.page);
        if (arr)
            arr.push(item);
        else
            byPage.set(item.page, [item]);
    }
    const result = new Map();
    for (const [page, pageItems] of byPage) {
        const heights = pageItems
            .map((it) => it.height)
            .filter((h) => h > 0)
            .sort((a, b) => a - b);
        const median = heights.length === 0 ? 12 : (heights[Math.floor(heights.length / 2)] ?? 12);
        // Distinct heights bucketed at 0.5pt resolution to merge near-identical sizes.
        const seen = new Set();
        for (const h of heights)
            seen.add(Math.round(h * 2) / 2);
        const distinct = Array.from(seen).sort((a, b) => b - a);
        result.set(page, { page, medianHeight: median, distinctHeights: distinct });
    }
    return result;
}
/** Document-wide median across all pages — useful for cross-page heading thresholds. */
export function documentMedianHeight(stats) {
    const allMedians = Array.from(stats.values()).map((s) => s.medianHeight);
    if (allMedians.length === 0)
        return 12;
    allMedians.sort((a, b) => a - b);
    return allMedians[Math.floor(allMedians.length / 2)] ?? 12;
}
//# sourceMappingURL=font-analysis.js.map